import { useEffect, useState } from 'react'

import { formatMoney } from '../formatting/money'
import { formatMoneyDelta } from './formatters'

/**
 * A discrete operating range for the money stepper. The stepper steps by
 * `bucket.step`; as money crosses a bucket boundary the next render re-anchors
 * onto the new bucket so the step size scales with magnitude.
 */
type Bucket = {
  min: number
  max: number
  step: number
}

const BUCKETS: Bucket[] = [
  { min: 0, max: 1_000, step: 10 },
  { min: 1_000, max: 10_000, step: 500 },
  { min: 10_000, max: 100_000, step: 1_000 },
  { min: 100_000, max: 1_000_000, step: 10_000 },
  { min: 1_000_000, max: 10_000_000, step: 100_000 },
]

const MONEY_FLOOR = 0
const MONEY_CEILING = BUCKETS[BUCKETS.length - 1].max

export const bucketIndexFor = (value: number): number => {
  const abs = Math.abs(value)
  if (abs < 1_000) return 0
  if (abs < 10_000) return 1
  if (abs < 100_000) return 2
  if (abs < 1_000_000) return 3
  return 4
}

export type UseMoneyAdjustmentArgs = {
  baseline: number
  ratio: number
  onRatioChange: (ratio: number) => void
}

export type UseMoneyAdjustmentResult = {
  moneyValue: number
  bucket: Bucket
  formatValue: (n: number) => string
  formatDelta: (mDiff: number) => string
  handleIncrement: () => void
  handleDecrement: () => void
  canIncrement: boolean
  canDecrement: boolean
}

/**
 * Headless logic for a money-valued adjustment stepper:
 * - Ratio (-1..N) is the source of truth; money is derived as
 *   `round(baseline * (1 + ratio))`.
 * - Each press steps by `bucket.step` within the bucket containing the
 *   current money value; the bucket re-anchors on the next render once the
 *   value crosses a boundary, so step magnitude scales with the value.
 * - Decrements clamp at `MONEY_FLOOR` (0) and increments clamp at
 *   `MONEY_CEILING` (top bucket max).
 */
export const useMoneyAdjustment = ({
  baseline,
  ratio,
  onRatioChange,
}: UseMoneyAdjustmentArgs): UseMoneyAdjustmentResult => {
  const moneyValue = Math.round(baseline * (1 + ratio))

  const [bucketIndex, setBucketIndex] = useState(() =>
    bucketIndexFor(moneyValue)
  )

  const stateBucket = BUCKETS[bucketIndex]

  const inStateBucket =
    moneyValue >= stateBucket.min && moneyValue <= stateBucket.max

  const effectiveIndex = inStateBucket
    ? bucketIndex
    : bucketIndexFor(moneyValue)

  const bucket = BUCKETS[effectiveIndex]

  const formatValue = (m: number): string => formatMoney(m, 'precise')

  const formatDelta = (mDiff: number): string =>
    formatMoneyDelta(mDiff, baseline)

  const stepTo = (next: number): void => {
    const clamped = Math.min(MONEY_CEILING, Math.max(MONEY_FLOOR, next))
    onRatioChange((clamped - baseline) / baseline)
  }

  const handleIncrement = (): void => stepTo(moneyValue + bucket.step)
  const handleDecrement = (): void => stepTo(moneyValue - bucket.step)

  const canIncrement = moneyValue < MONEY_CEILING
  const canDecrement = moneyValue > MONEY_FLOOR

  useEffect(() => {
    if (effectiveIndex !== bucketIndex) setBucketIndex(effectiveIndex)
  }, [effectiveIndex, bucketIndex])

  return {
    moneyValue,
    bucket,
    formatValue,
    formatDelta,
    handleIncrement,
    handleDecrement,
    canIncrement,
    canDecrement,
  }
}
